import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  input,
  Input,
  Output,
  type OnDestroy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IntegrationType,
  type IIntegration,
} from '@principle-theorem/integrations';
import { IsManagementDirective } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  ButtonsContainerComponent,
  isDisabled$,
  MapPipe,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { TwilioIntegrationStorage } from '@principle-theorem/principle-core';
import {
  IBaseSMSIntegrationData,
  IOrganisation,
  SmsIntegrationOption,
  type IPractice,
  type ITwilioIntegrationData,
  type ITwilioPracticeNumber,
} from '@principle-theorem/principle-core/interfaces';
import {
  getDoc,
  isChanged$,
  isSameRef,
  multiMap,
  multiSwitchMap,
  Region,
  snapshot,
  WithRef,
  type CollectionReference,
  type INamedDocument,
} from '@principle-theorem/shared';
import {
  BehaviorSubject,
  from,
  ReplaySubject,
  Subject,
  type Observable,
} from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { StripeSMSPricingSelectorComponent } from '../stripe-sms-pricing-selector/stripe-sms-pricing-selector.component';
import { type TwilioPracticeNumberFormGroup } from './phone-numbers-form';
import { PracticePhoneNumbersInputComponent } from './practice-phone-numbers-input/practice-phone-numbers-input.component';
import { TwilioBillingSummaryComponent } from './twilio-billing-summary/twilio-billing-summary.component';

interface IPhoneNumberSummary
  extends Pick<ITwilioPracticeNumber, 'phoneNumber' | 'disabled'> {
  practice: INamedDocument<IPractice>;
}

type BasicFormData = Pick<ITwilioIntegrationData, 'testModeEnabled'>;

type ManagementFormData = Pick<
  ITwilioIntegrationData,
  'enabled' | 'authToken' | 'accountSid'
>;

@Component({
  selector: 'pr-manage-twilio-integration',
  templateUrl: './manage-twilio-integration.component.html',
  styleUrls: ['./manage-twilio-integration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSlideToggle,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    MapPipe,
    IsManagementDirective,
    ButtonsContainerComponent,
    PracticePhoneNumbersInputComponent,
    TwilioBillingSummaryComponent,
    CdkCopyToClipboard,
    StripeSMSPricingSelectorComponent,
  ],
})
export class ManageTwilioIntegrationComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _storage = new TwilioIntegrationStorage();
  private _integrationsCol$ = new ReplaySubject<
    CollectionReference<IIntegration<ITwilioIntegrationData>>
  >(1);
  private _savedProvider$ = new ReplaySubject<SmsIntegrationOption | undefined>(
    1
  );

  organisation = input.required<WithRef<IOrganisation>>();
  trackByIndex = TrackByFunctions.index();
  isManagement$: Observable<boolean>;
  phoneNumbers$: Observable<IPhoneNumberSummary[]>;
  form = new TypedFormGroup<BasicFormData>({
    testModeEnabled: new TypedFormControl<boolean>(false),
  });
  managementForm = new TypedFormGroup<ManagementFormData>({
    enabled: new TypedFormControl<boolean>(false),
    authToken: new TypedFormControl<string>(undefined),
    accountSid: new TypedFormControl<string>(undefined),
  });
  pricingUpdate = new BehaviorSubject<IBaseSMSIntegrationData | undefined>(
    undefined
  );
  data$: Observable<ITwilioIntegrationData>;
  region = input.required<Region>();

  @Output() providerChanged = new EventEmitter<SmsIntegrationOption>();

  @Input()
  set integrationsCol(
    integrationsCol: CollectionReference<IIntegration<ITwilioIntegrationData>>
  ) {
    if (integrationsCol) {
      this._integrationsCol$.next(integrationsCol);
    }
  }

  @Input()
  set savedProvider(provider: SmsIntegrationOption | undefined) {
    this._savedProvider$.next(provider);
  }

  constructor(
    private _dialog: BasicDialogService,
    public snackBar: MatSnackBar
  ) {
    this.data$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) => this._storage.get$(integrationsCol)),
      map((data) => data?.data ?? TwilioIntegrationStorage.init({}))
    );

    this.data$.pipe(takeUntil(this._onDestroy$)).subscribe((data) => {
      this.form.patchValue(data, { emitEvent: false });
      this.managementForm.patchValue(data, { emitEvent: false });
    });

    this.phoneNumbers$ = this.data$.pipe(
      map((data) => data.phoneNumbers ?? []),
      multiSwitchMap((phoneNumber) =>
        from(getDoc(phoneNumber.practiceRef)).pipe(
          map((practice) => ({
            ...phoneNumber,
            practice,
          }))
        )
      )
    );

    validFormGroupChanges$(this.form)
      .pipe(isChanged$(), takeUntil(this._onDestroy$))
      .subscribe(() => void this.save());
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async addPhoneNumber(form: TwilioPracticeNumberFormGroup): Promise<void> {
    const phoneNumber = form.getRawValue();
    const data = await this._getCurrentData();
    const col = await snapshot(this._integrationsCol$);
    await this._storage.upsert(col, {
      ...data,
      phoneNumbers: [...(data.phoneNumbers ?? []), phoneNumber],
    });
    form.reset();
    form.updateValueAndValidity();
  }

  async archivePhoneNumber(phoneNumber: IPhoneNumberSummary): Promise<void> {
    const confirmed = await this._dialog.confirm({
      prompt:
        'Are you sure you want to archive this number? Messages can still be recieved via this number however this number will no longer be used to send messages.',
      title: 'Archive Number',
      submitLabel: 'Yes',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    const data = await this._getCurrentData();
    const col = await snapshot(this._integrationsCol$);

    const phoneNumbers: ITwilioPracticeNumber[] = (data.phoneNumbers ?? []).map(
      (currentNumber) => {
        const isSame =
          currentNumber.phoneNumber === phoneNumber.phoneNumber &&
          isSameRef(currentNumber.practiceRef, phoneNumber.practice.ref);
        if (!isSame) {
          return currentNumber;
        }
        return {
          ...currentNumber,
          disabled: true,
        };
      }
    );

    await this._storage.upsert(col, {
      ...data,
      phoneNumbers,
    });
  }

  async save(): Promise<void> {
    const savedProvider = await snapshot(this._savedProvider$);
    if (savedProvider !== IntegrationType.Twilio) {
      this.providerChanged.emit(IntegrationType.Twilio);
    }

    const col = await snapshot(this._integrationsCol$);
    const data = await this._getCurrentData();
    await this._storage.upsert(col, data);
    this.snackBar.open('Settings Updated');
    this.managementForm.markAsPristine();
    this.managementForm.updateValueAndValidity();
  }

  addPhoneNumberDisabled$(
    form: TwilioPracticeNumberFormGroup
  ): Observable<boolean> {
    return isDisabled$(form);
  }

  updatePricing(pricing: IBaseSMSIntegrationData): void {
    this.pricingUpdate.next(pricing);
    this.form.markAsTouched();
  }

  private async _getCurrentData(): Promise<ITwilioIntegrationData> {
    const data = this.form.getRawValue();
    const managementData = this.managementForm.getRawValue();
    const pricing = await snapshot(this.pricingUpdate);
    const phoneNumbers = await snapshot(
      this.phoneNumbers$.pipe(
        multiMap(({ phoneNumber, practice, disabled }) => ({
          phoneNumber,
          practiceRef: practice.ref,
          disabled,
        }))
      )
    );
    return { ...data, ...managementData, ...(pricing || {}), phoneNumbers };
  }
}
